import { http } from "./http";

export type NotificationType =
  | "APPOINTMENT_BOOKED"
  | "APPOINTMENT_CANCELLED"
  | "APPOINTMENT_REMINDER"
  | "TREATMENT_COMPLETED"
  | "DOCTOR_JOINED"
  | "PRESCRIPTION_READY"
  | "MEDICATION"
  | "LAB_RESULT"
  | "VACCINE"
  | "RECEIPT"
  | "PROMO"
  | "SECURITY";

export type NotificationSeverity = "INFO" | "WARNING" | "URGENT";

export type NotificationCategory =
  | "all"
  | "appointment"
  | "medication"
  | "lab"
  | "vaccine"
  | "receipt"
  | "promo"
  | "security";

export interface NotifyAction {
  id: string;
  label: string;
  variant?: "primary" | "secondary";
}

export interface NotificationView {
  id: string;
  type: NotificationType;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  actions: NotifyAction[];
  linkText: string | null;
  severity: NotificationSeverity;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationListResponse {
  items: NotificationView[];
  hasMore: boolean;
}

export interface ListOpts {
  unreadOnly?: boolean;
  categories?: NotificationCategory[];
  before?: string;
  limit?: number;
}

export interface NotificationPreferenceView {
  type: NotificationType;
  inApp: boolean;
  email: boolean;
  sms: boolean;
  /** Phase 3.8 — quiet hours window (minutes since midnight Bangkok-local). */
  quietHoursStart: number | null;
  quietHoursEnd: number | null;
}

export interface SetPreferenceInput {
  type: NotificationType;
  inApp: boolean;
  email?: boolean;
  sms?: boolean;
  quietHoursStart?: number | null;
  quietHoursEnd?: number | null;
}

export const notificationApi = {
  async list(opts: ListOpts = {}): Promise<NotificationListResponse> {
    const params: Record<string, string | number> = {};
    if (opts.unreadOnly) params.unreadOnly = "true";
    if (opts.categories?.length) params.categories = opts.categories.join(",");
    if (opts.before) params.before = opts.before;
    if (opts.limit) params.limit = opts.limit;
    const { data } = await http.get<NotificationListResponse>(
      "/public/notification",
      { params },
    );
    return data;
  },

  async countUnread(): Promise<{ count: number }> {
    const { data } = await http.get<{ count: number }>(
      "/public/notification/count-unread",
    );
    return data;
  },

  async markRead(id: string): Promise<void> {
    await http.patch(`/public/notification/${id}/read`);
  },

  async markAllRead(): Promise<{ updated: number }> {
    const { data } = await http.post<{ updated: number }>(
      "/public/notification/mark-all-read",
    );
    return data;
  },

  async dismiss(id: string): Promise<void> {
    await http.patch(`/public/notification/${id}/dismiss`);
  },

  // ---- Phase 3.7 — preferences ----

  async getPreferences(): Promise<NotificationPreferenceView[]> {
    const { data } = await http.get<NotificationPreferenceView[]>(
      "/public/notification/preference",
    );
    return data;
  },

  async setPreference(
    input: SetPreferenceInput,
  ): Promise<NotificationPreferenceView> {
    const { data } = await http.put<NotificationPreferenceView>(
      "/public/notification/preference",
      input,
    );
    return data;
  },

  async resetPreferences(): Promise<{ cleared: number }> {
    const { data } = await http.post<{ cleared: number }>(
      "/public/notification/preference/reset",
    );
    return data;
  },
};
